<script setup lang="ts">
import { AuthSocialAuth } from '#components';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import type { AppsflyerLoginMethod } from '@/types/Metrics/trackers/appsflyer';

// --- Abas ---
const layoutStore = useLayoutStore();
const { authInitialTab, authComplianceMode } = storeToRefs(layoutStore);
const activeTab = ref<'login' | 'register'>(authInitialTab.value || 'login');

watch(activeTab, (newTab: 'login' | 'register') => {
  authInitialTab.value = newTab;
});

// --- Login logic ---
const { $metrics } = useNuxtApp();
const { t } = useTexts();
const {
  login,
  logout,
  forgotPassword,
  setRecoveryPasswordFlow,
  setRecoveryPasswordData,
} = useAuthStore();

const { openToaster } = useLayoutStore();
const { visitorOrUserCountry } = useParameters();
const { userCountry } = useUserData();
const { currentRouteName, getSecureQueryParam } = useRoutes();
const {
  openForgotPassword,
  openRecoveryPasswordOptions,
  isCompleteRegulatoryRegisterEnabled,
  isRegulatorySimplifiedRegisterEnabled,
} = useAuth();

const { isAppSource } = useLayout();
const { userCanOnlyWithdraw } = useUserLimits();
const { isCaptchaLoginEnabled, isInvisibleCaptcha } = useCaptcha();
const {
  isAvailableEnvironment: isNativeAppEnv,
  checkAvailability: checkBiometricAvailability,
  unlock: unlockBiometric,
  isLockedFlagSet,
  isDisabledFlagSet,
} = useBiometricAuth();

const { rememberLastCredentials } = useBiometricEnrollment();

// Compliance (ENG-404): no fluxo de login/registro. Quando a aba é registro e
// o tipo ativo é o CompleteRegulatory, o aviso vem de DENTRO daquele componente
// — aqui escondemos pra não duplicar. Nos demais casos (form de login, registro
// não-regulatório) o aviso é renderizado por este bloco.
const isRegulatoryRegisterActive = computed(
  () =>
    activeTab.value === 'register' &&
    (isCompleteRegulatoryRegisterEnabled.value ||
      isRegulatorySimplifiedRegisterEnabled.value),
);

const showLoginCompliance = computed(
  () => authComplianceMode.value && !isRegulatoryRegisterActive.value,
);

const route = useRoute();

// --- Biometria (UX Betano) ---
const isBiometricButtonVisible = ref(false);
const isBiometricButtonLoading = ref(false);
const isBiometricLocked = ref(false);
const biometricMaskedLogin = ref<string | null>(null);

const biometricButtonLabel = computed(() => {
  const verb = 'Entrar com biometria';

  if (biometricMaskedLogin.value) {
    return `${verb} • ${biometricMaskedLogin.value}`;
  }

  return verb;
});

async function evaluateBiometric() {
  if (!isNativeAppEnv()) return;

  const availability = await checkBiometricAvailability();

  const ready =
    availability.hasHardware &&
    availability.isEnrolled &&
    availability.hasCredential &&
    !isDisabledFlagSet();

  biometricMaskedLogin.value = availability.maskedLogin;
  isBiometricLocked.value = ready && isLockedFlagSet();
  isBiometricButtonVisible.value = ready;
}

async function onBiometricLogin() {
  if (isBiometricButtonLoading.value) return;
  if (isBiometricLocked.value) {
    openToaster({
      message:
        'Biometria bloqueada. Faça login com sua senha para liberá-la novamente.',
      type: 'warning',
    });

    return;
  }

  isBiometricButtonLoading.value = true;

  const result = await unlockBiometric();

  isBiometricButtonLoading.value = false;

  if (!result.success || !result.login || !result.password) {
    if (result.error === 'not_enrolled') {
      isBiometricButtonVisible.value = false;
      biometricMaskedLogin.value = null;
    } else if (result.error === 'biometric_changed') {
      isBiometricButtonVisible.value = false;
      biometricMaskedLogin.value = null;
      openToaster({
        message:
          'Detectamos uma alteração na biometria do aparelho. Faça login e ative a biometria novamente.',
        type: 'warning',
      });
    } else if (result.error && result.error !== 'cancelled') {
      openToaster({
        message: 'Não foi possível entrar com biometria.',
        type: 'error',
      });
    }

    return;
  }

  formValueLogin.value = result.login;
  formValuePassword.value = result.password;

  await nextTick();
  submitFormLogin(undefined, 'biometric');
}

const isLoadingLogin = ref(false);
const isReactivateAccountDialogVisible = ref(false);
const isForceChangePasswordVisible = ref(false);

const formValueLogin = ref('');
const formValuePassword = ref('');

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);
const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const isLoginCpf = computed(() => {
  const digits = formValueLogin.value.replace(/\D/g, '');

  return validateCpf(digits);
});

const captchaComponentClass = computed(() => {
  if (!isCaptchaLoginEnabled.value)
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  if (isInvisibleCaptcha.value) return 'mt-0 relative';

  return 'mt-2 -mb-4';
});

const loginFieldLabel = computed(() =>
  visitorOrUserCountry.value === 'BRA'
    ? t('auth.login.email_or_cpf')
    : t('auth.login.email_or_doc'),
);

const activeSubmitButton = computed(() => {
  if (formValueLogin.value.length < 5 || formValuePassword.value.length < 5)
    return false;
  if (isCaptchaLoginEnabled.value && !antiRobotToken.value) return false;

  return true;
});

const onCloseReactivateAccountDialog = () => {
  isReactivateAccountDialogVisible.value = false;
  antiRobotRef.value?.$resetAllCaptcha();
};

const onCloseForceChangePassword = () => {
  isForceChangePasswordVisible.value = false;

  if (isLoginCpf.value && userCountry.value === 'BRA') {
    isLoadingLogin.value = true;

    const documentDigits = formValueLogin.value.replace(/\D/g, '');

    forgotPassword({
      document: documentDigits,
      captcha_token: antiRobotToken.value,
    })
      .then((data) => {
        if (data.status.toString().startsWith('20')) {
          setRecoveryPasswordFlow(data.data);
          setRecoveryPasswordData({ login: documentDigits });
          openRecoveryPasswordOptions();
        }
      })
      .finally(() => {
        isLoadingLogin.value = false;
      });

    return;
  }

  openForgotPassword();
};

const submitFormLogin = (
  activate_account?: boolean,
  loginMethod: AppsflyerLoginMethod = 'email',
) => {
  if (isCaptchaLoginEnabled.value && !antiRobotToken.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }
  if (!formValueLogin.value || formValueLogin.value.length < 5) {
    openToaster({ message: loginFieldLabel.value, type: 'error' });

    return;
  }
  if (!formValuePassword.value || formValuePassword.value.length < 6) {
    openToaster({
      message: t('auth.login.error.min_length_password'),
      type: 'error',
    });

    return;
  }

  const submitData: ILoginSubmitData = {
    login: formValueLogin.value,
    email: formValueLogin.value, // remover quando API atualizar
    password: formValuePassword.value,
    app_source: isAppSource.value ? 'app' : 'web',
    captcha_token: antiRobotToken.value,
    activate_account,
  };

  try {
    localStorage.setItem('temp_login_email', formValueLogin.value);
  } catch {}

  // Guarda credenciais em memória para oferecer opt-in biométrico em
  // seguida (caso o login dê certo). Vive só no escopo da SPA, sem persistir.
  if (isNativeAppEnv()) {
    rememberLastCredentials(formValueLogin.value, formValuePassword.value);
  }

  isLoadingLogin.value = true;
  setTimeout(() => submitApiLogin(submitData, loginMethod), 200);
};

const submitApiLogin = async (
  submitData: ILoginSubmitData,
  loginMethod: AppsflyerLoginMethod,
) => {
  await logout({ displayMessage: false, redirectToHome: false });

  await login(submitData)
    .then(async (response) => {
      $metrics
        .push('gtm')
        ?.handle({ event: 'login', ...gtmUserParams(response.data.user) });

      $metrics.push('appsflyer')?.handle('af_login', {
        af_login_method: loginMethod,
        _customer_user_id: response.data?.user?.id,
      });

      const { isOnBetBrWithoutMigrateAccepted } = useUserValidations();

      if (isOnBetBrWithoutMigrateAccepted.value) {
        return await navigateTo(localeLink('/migrate/confirm'));
      }

      if (userCanOnlyWithdraw.value) {
        await navigateTo(localeLink('/user/wallet'));
      } else if (currentRouteName.value === 'login') {
        await navigateTo(localeLink('/'));
      }
    })
    .catch((err) => {
      const errorData = err?.data;

      isLoadingLogin.value = false;

      if (errorData?.type === 'cancelled_account') {
        isReactivateAccountDialogVisible.value = true;
      }
      if (errorData.type === 'change_weak_password_required') {
        isForceChangePasswordVisible.value = true;
      }
    })
    .finally(() => {
      antiRobotRef.value?.$resetAllCaptcha();
      if (currentRouteName.value !== 'login') isLoadingLogin.value = false;
    });
};

const tempLoginEmail = ref<string | null>(null);

onMounted(() => {
  const tabFromQuery = route.query.tab
    ? getSecureQueryParam(route.query.tab)
    : '';

  if (tabFromQuery === 'register' || authInitialTab.value === 'register') {
    activeTab.value = 'register';
  }

  const fromQuery = route.query.email
    ? getSecureQueryParam(route.query.email)
    : '';

  tempLoginEmail.value =
    fromQuery || localStorage.getItem('temp_login_email') || '';
  if (tempLoginEmail.value) formValueLogin.value = tempLoginEmail.value;

  evaluateBiometric();
});
</script>

<template>
  <ClientOnly>
    <div :class="$style.pageContainer">
      <!-- force change password -->
      <AuthLoginForceChangePassword
        v-if="isForceChangePasswordVisible"
        @close="onCloseForceChangePassword"
      />

      <!-- Abas -->
      <div
        class="w-full self-stretch h-[50px] p-[7px] bg-[#0C1B44] rounded-[10px] flex items-center gap-[10px] text-white"
      >
        <button
          :aria-selected="activeTab === 'login'"
          :class="[
            'flex-1 self-stretch min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-[10px] inline-flex justify-center items-center gap-2 sm:gap-2.5 transition',
            activeTab === 'login' ? 'bg-[#0485f7]' : 'bg-transparent',
          ]"
          type="button"
          @click="activeTab = 'login'"
        >
          <span class="text-sm sm:text-base font-normal leading-5 truncate"
            >Entrar</span
          >
        </button>

        <button
          :aria-selected="activeTab === 'register'"
          :class="[
            'flex-1 self-stretch min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-[10px] inline-flex justify-center items-center gap-2 sm:gap-2.5 transition',
            activeTab === 'register' ? 'bg-[#0485f7]' : 'bg-transparent',
          ]"
          type="button"
          @click="activeTab = 'register'"
        >
          <span class="text-sm sm:text-base font-normal leading-5 truncate"
            >Criar nova conta</span
          >
        </button>
      </div>

      <!-- Conteúdo -->
      <form
        v-if="activeTab === 'login'"
        class="w-full flex flex-col gap-3 mt-5"
        @submit.prevent="submitFormLogin()"
      >
        <BaseLoading
          class="!bg-auth-bg-primary"
          :icon-loader="true"
          :show="isLoadingLogin"
        />

        <AuthLoginInput
          id="login"
          v-model="formValueLogin"
          autocomplete="off"
          :initial-value="tempLoginEmail || ''"
          :label="loginFieldLabel"
          name="login"
          placeholder="E-mail ou CPF"
          type="login"
        />

        <div class="password-field">
          <AuthLoginInput
            id="password"
            v-model="formValuePassword"
            autocomplete="current-password"
            :label="$t('auth.login.password')"
            name="password"
            placeholder="Senha"
            :show-hide-pass="true"
          />
        </div>

        <div class="self-stretch text-left">
          <a
            class="self-stretch text-left justify-center text-[#fcfcfc] text-sm font-medium px-3 py-1.5 rounded-xl cursor-pointer transition-colors duration-150 hover:text-white decoration-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            href="#"
            @click.prevent="openForgotPassword()"
          >
            {{ $t('auth.login.forgot_password') }}
          </a>
        </div>

        <div :class="$style.buttonBox">
          <AuthLoginSubmitButton
            :disabled="!activeSubmitButton"
            :label="$t('auth.login.login')"
            type="submit"
          />
        </div>

        <button
          v-if="isBiometricButtonVisible && !isBiometricLocked"
          class="mt-2 w-full rounded-lg border border-white/20 bg-transparent px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/5 disabled:opacity-50"
          :disabled="isBiometricButtonLoading"
          type="button"
          @click="onBiometricLogin"
        >
          {{ isBiometricButtonLoading ? 'Verificando…' : biometricButtonLabel }}
        </button>

        <p
          v-else-if="isBiometricButtonVisible && isBiometricLocked"
          class="mt-2 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-center text-xs text-white/60"
        >
          Para usar a biometria novamente, faça login com sua senha.
        </p>

        <div :class="captchaComponentClass">
          <BaseFormAntiRobot ref="antiRobotRef" />
        </div>

        <!-- Social -->
        <AuthSocialAuth
          :is-login="true"
          :with-titles="true"
        />

        <!-- Reactivar conta -->
        <LazyBaseModalReactivateAccount
          v-model="isReactivateAccountDialogVisible"
          @close="onCloseReactivateAccountDialog"
          @reactivate="submitFormLogin(true)"
        />
      </form>

      <div
        v-else
        class="w-full self-stretch"
      >
        <AuthRegisterCompleteRegulatory
          v-if="
            isCompleteRegulatoryRegisterEnabled ||
            isRegulatorySimplifiedRegisterEnabled
          "
        />

        <AuthRegisterSingleStep v-else />
      </div>

      <BaseComplianceInfo
        v-if="showLoginCompliance"
        class="w-full mt-2"
      />
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.pageContainer {
  @apply w-full p-0 flex flex-col justify-start md:justify-center items-center overflow-x-hidden;
}

.buttonBox {
  @apply relative w-full;
  .btnSubmit {
    @apply rounded flex items-center justify-center gap-2 bg-auth-button-bg text-auth-button-text text-base font-semibold w-full px-3 py-3;
    background: radial-gradient(
      138% 168.3% at 36.76% -16.67%,
      #ff704a 0%,
      #ff3400 100%
    );
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
    svg {
      @apply text-sm;
    }
  }
}

/* Ajusta o visual do input do Login e corrige autofill/focus do navegador */
:deep(.input-group) {
  background-color: #0c1b44 !important;
  border-radius: 10px !important;
  height: 3rem;
  display: flex;
  align-items: center;
}

:deep(.input-group .group) {
  background: transparent !important;
}

:deep(.input-group .input) {
  background-color: transparent !important;
  color: #cbd5e1 !important;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  line-height: 1.5rem;
}

:deep(.input-group .input::placeholder) {
  color: #94a3b8 !important;
}

/* Força o mesmo fundo no autofill e no foco para evitar fundo cinza */
:deep(.input-group .input:-webkit-autofill),
:deep(.input-group .input:-webkit-autofill:hover),
:deep(.input-group .input:-webkit-autofill:focus) {
  -webkit-box-shadow: 0 0 0px 1000px #0c1b44 inset !important;
  -webkit-text-fill-color: #cbd5e1 !important;
  caret-color: #cbd5e1 !important;
  transition: background-color 9999s ease-out 0s !important;
}
</style>
